import React, { useState, useEffect } from 'react';
import { Settings, Bell, BellOff, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const MessagePreferences = ({ competitions = [] }) => {
  const [preferences, setPreferences] = useState({
    global_enabled: true,
    competition_preferences: {},
    social_feed_preferences: {}
  });
  const [loading, setLoading] = useState(true);
  const [activeCompetitions, setActiveCompetitions] = useState([]);

  useEffect(() => {
    fetchPreferences();
    fetchActiveCompetitions();
  }, []);

  const fetchActiveCompetitions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/player/my-competitions`, {
        withCredentials: true
      });
      setActiveCompetitions(response.data);
    } catch (error) {
      console.error('Error fetching active competitions:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/player/message-preferences`, {
        withCredentials: true
      });
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
      toast.error('Failed to load message preferences');
    } finally {
      setLoading(false);
    }
  };

  const updateGlobalPreference = async (enabled) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/player/message-preferences`,
        { global_enabled: enabled },
        { withCredentials: true }
      );
      setPreferences({ ...preferences, global_enabled: enabled });
      toast.success(`Messages ${enabled ? 'enabled' : 'disabled'} globally`);
    } catch (error) {
      console.error('Error updating global preference:', error);
      toast.error('Failed to update preference');
    }
  };

  const updateCompetitionPreference = async (competitionId, enabled) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/player/message-preferences`,
        {
          competition_id: competitionId,
          competition_enabled: enabled
        },
        { withCredentials: true }
      );
      setPreferences({
        ...preferences,
        competition_preferences: {
          ...preferences.competition_preferences,
          [competitionId]: enabled
        }
      });
      toast.success(`Competition messages ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating competition preference:', error);
      toast.error('Failed to update preference');
    }
  };

  const updateSocialFeedPreference = async (competitionId, enabled) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/player/message-preferences`,
        {
          competition_id: competitionId,
          social_feed_enabled: enabled
        },
        { withCredentials: true }
      );
      setPreferences({
        ...preferences,
        social_feed_preferences: {
          ...preferences.social_feed_preferences,
          [competitionId]: enabled
        }
      });
      toast.success(`Social feed ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating social feed preference:', error);
      toast.error('Failed to update preference');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6" data-testid="message-preferences">
      <div className="flex items-center gap-2 mb-6">
        <Settings className="w-6 h-6 text-accent" />
        <h2 className="text-xl font-semibold text-ink tracking-tight">Message Preferences</h2>
      </div>

      {/* Global Setting */}
      <div className="mb-6 p-4 border border-line rounded-lg bg-app">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {preferences.global_enabled ? (
              <Bell className="w-5 h-5 text-accent" />
            ) : (
              <BellOff className="w-5 h-5 text-ink-soft" />
            )}
            <div>
              <h3 className="font-semibold text-lg">All Admin Messages</h3>
              <p className="text-sm text-ink-muted">
                {preferences.global_enabled 
                  ? 'You are receiving messages from admins' 
                  : 'Messages from admins are disabled'}
              </p>
            </div>
          </div>
          <button
            onClick={() => updateGlobalPreference(!preferences.global_enabled)}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              preferences.global_enabled
                ? 'bg-ink text-white hover:bg-ink-hover'
                : 'bg-line-strong text-ink-muted hover:bg-line-strong'
            }`}
            data-testid="toggle-global"
          >
            {preferences.global_enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>

      {/* Competition-Specific Settings */}
      <div>
        <h3 className="font-semibold text-lg mb-4 text-ink">Active Competition Settings</h3>
        {activeCompetitions.length === 0 ? (
          <p className="text-ink-soft text-center py-4">No active competitions</p>
        ) : (
          <div className="space-y-4">
            {activeCompetitions.map((comp) => {
              const messagesEnabled = preferences.competition_preferences[comp.id] !== false;
              const socialFeedEnabled = preferences.social_feed_preferences[comp.id] !== false;
              return (
                <div
                  key={comp.id}
                  className="p-4 border border-line rounded-lg hover:border-accent transition"
                  data-testid={`comp-pref-${comp.id}`}
                >
                  <div className="mb-3">
                    <h4 className="font-medium text-ink text-lg">{comp.name}</h4>
                    <p className="text-sm text-ink-muted">
                      {comp.start_date} - {comp.end_date}
                    </p>
                  </div>
                  
                  {/* Messages Toggle */}
                  <div className="flex items-center justify-between mb-2 p-2 bg-app rounded">
                    <div>
                      <span className="text-sm font-medium text-ink-muted">Admin Messages</span>
                      <p className="text-xs text-ink-soft">Receive messages from admins</p>
                    </div>
                    <button
                      onClick={() => updateCompetitionPreference(comp.id, !messagesEnabled)}
                      className={`px-3 py-1 rounded text-sm font-medium transition ${
                        messagesEnabled
                          ? 'bg-accent-tint text-ink hover:bg-accent-tint'
                          : 'bg-line text-ink-muted hover:bg-line-strong'
                      }`}
                      disabled={!preferences.global_enabled}
                      data-testid={`toggle-comp-${comp.id}`}
                    >
                      {messagesEnabled ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  
                  {/* Social Feed Toggle */}
                  <div className="flex items-center justify-between p-2 bg-app rounded">
                    <div>
                      <span className="text-sm font-medium text-ink-muted">Social Feed</span>
                      <p className="text-xs text-ink-soft">Participate in competition chat</p>
                    </div>
                    <button
                      onClick={() => updateSocialFeedPreference(comp.id, !socialFeedEnabled)}
                      className={`px-3 py-1 rounded text-sm font-medium transition ${
                        socialFeedEnabled
                          ? 'bg-accent-tint text-accent-deep hover:bg-accent-tint'
                          : 'bg-line text-ink-muted hover:bg-line-strong'
                      }`}
                      data-testid={`toggle-social-${comp.id}`}
                    >
                      {socialFeedEnabled ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!preferences.global_enabled && (
        <div className="mt-4 p-3 bg-warm-tint/40 border border-warm/40 rounded-lg">
          <p className="text-sm text-warm">
            <strong>Note:</strong> Global messages are disabled. Enable them to receive competition-specific messages.
          </p>
        </div>
      )}
    </div>
  );
};

export default MessagePreferences;
